import { MessageCircle, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const servicePages: Record<string, string> = {
  "/psicoterapia-adulto": "Psicoterapia Adulto",
  "/psicoterapia-infantil": "Psicoterapia Infantil",
  "/psicoterapia-adolescente": "Psicoterapia Adolescente",
  "/psicoterapia-melhor-idade": "Psicoterapia Melhor Idade",
  "/psicoterapia-casal": "Psicoterapia de Casal",
  "/psicoterapia-breve": "Psicoterapia Breve",
  "/psicoterapia-tcc": "Psicoterapia TCC",
  "/orientacao-vocacional": "Orientação Vocacional",
  "/reorientacao-carreira": "Reorientação de Carreira",
  "/avaliacao-psicodiagnostico": "Psicodiagnóstico",
  "/avaliacao-neuropsicologica": "Avaliação Neuropsicológica",
  "/avaliacao-laudo-cirurgia": "Laudo para Cirurgia",
  "/pericia-psicologica": "Perícia Psicológica",
};

const otherPages: Record<string, string> = {
  "/": "Home",
  "/contato": "Contato",
};

/**
 * WhatsAppButton — Marketing psychology:
 * - Pattern interrupt (pulsing halo) chama atenção sem manipular.
 * - Label proativo após 8s reduz fricção com micro-copy de baixo compromisso
 *   ("Manda um oi") — princípio de Fogg: aumentar ease + trigger claro.
 * - Fechável (dismiss) respeita autonomia — pattern ético.
 */
const WhatsAppButton = () => {
  const location = useLocation();
  const serviceName = servicePages[location.pathname];
  const otherName = otherPages[location.pathname];
  const contextName = serviceName || otherName || "Site";
  const message = encodeURIComponent(
    `Oi! Vim pelo site da Equilybra (${contextName}) e gostaria de saber como funciona.`
  );

  const [showLabel, setShowLabel] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (dismissed) return;
    const t = setTimeout(() => setShowLabel(true), 8000);
    return () => clearTimeout(t);
  }, [dismissed]);

  return (
    <div className="fixed bottom-6 right-6 z-50 flex items-end gap-2">
      <AnimatePresence>
        {showLabel && !dismissed && (
          <motion.div
            initial={{ opacity: 0, x: 20, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 20, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            className="mb-2 relative bg-card border border-border rounded-2xl shadow-lg pl-4 pr-8 py-3 max-w-[220px]"
          >
            <button
              type="button"
              onClick={() => setDismissed(true)}
              aria-label="Fechar aviso"
              className="absolute top-1.5 right-1.5 p-1 rounded-full hover:bg-muted text-muted-foreground"
            >
              <X className="w-3.5 h-3.5" />
            </button>
            <p className="text-sm font-heading font-semibold text-foreground leading-tight">
              Manda um oi 👋
            </p>
            <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
              Sem compromisso — resposta em horário comercial.
            </p>
            <span className="absolute -bottom-1.5 right-6 w-3 h-3 bg-card border-r border-b border-border rotate-45" />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.a
        href={`https://wa.me/5511976974905?text=${message}`}
        target="_blank"
        rel="noopener noreferrer"
        className="relative w-16 h-16 rounded-full bg-whatsapp text-primary-foreground flex items-center justify-center shadow-lg hover:bg-whatsapp-hover transition-colors hover:scale-110 active:scale-95"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1, type: "spring", stiffness: 200 }}
        aria-label="Fale pelo WhatsApp"
      >
        <span className="absolute inset-0 rounded-full bg-whatsapp/60 animate-ping opacity-40" aria-hidden="true" />
        <MessageCircle className="w-7 h-7 relative z-10" />
      </motion.a>
    </div>
  );
};

export default WhatsAppButton;
